import { normalizeSearchQuery, searchQueryError, type ParcelSearchResponse, type ParcelSearchResult } from '../shared/parcelSearch';
import type { ParcelIdentifyResult } from './types';

/** Owns search requests and presentation; the existing map remains responsible for parcel selection. */
export function setupParcelSearch(options: {
  onOpen: () => void;
  onSelect: (parcel: ParcelIdentifyResult) => void;
}): { close: () => void; cancelSelection: () => void } {
  const root = document.querySelector<HTMLElement>('#parcelSearch')!;
  const toggle = document.querySelector<HTMLButtonElement>('#parcelSearchToggle')!;
  const content = document.querySelector<HTMLElement>('#parcelSearchContent')!;
  const form = document.querySelector<HTMLFormElement>('#parcelSearchForm')!;
  const input = document.querySelector<HTMLInputElement>('#parcelSearchInput')!;
  const clear = document.querySelector<HTMLButtonElement>('#parcelSearchClear')!;
  const panel = document.querySelector<HTMLElement>('#parcelSearchPanel')!;
  const status = document.querySelector<HTMLElement>('#parcelSearchStatus')!;
  const list = document.querySelector<HTMLOListElement>('#parcelSearchResults')!;
  const more = document.querySelector<HTMLButtonElement>('#parcelSearchMore')!;
  const retry = document.querySelector<HTMLButtonElement>('#parcelSearchRetry')!;
  let debounce: number | undefined;
  let searchRequest: AbortController | null = null;
  let selectionRequest: AbortController | null = null;
  let searchVersion = 0;
  let selectionVersion = 0;
  let results: ParcelSearchResult[] = [];
  let completedQuery = '';
  let completedStatus = '';
  let nextOffset: number | null = null;
  let retryAction: (() => void) | null = null;

  const setOpen = (open: boolean) => {
    content.hidden = !open;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? 'Close property search' : 'Open property search');
    toggle.title = open ? 'Close property search' : 'Open property search';
    input.setAttribute('aria-expanded', String(open));
    if (open) options.onOpen();
  };
  const stopSearch = () => {
    window.clearTimeout(debounce);
    searchRequest?.abort();
    searchVersion += 1;
    panel.removeAttribute('aria-busy');
    more.disabled = false;
  };
  const cancelSelection = () => {
    selectionVersion += 1;
    selectionRequest?.abort();
    if (selectionRequest) {
      status.textContent = completedStatus;
      panel.removeAttribute('aria-busy');
    }
    selectionRequest = null;
  };
  const close = (restoreFocus = false) => {
    stopSearch();
    cancelSelection();
    // Dismiss the keyboard when hiding the field, without stealing focus from
    // another map control or the selected parcel's heading.
    if (content.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
    setOpen(false);
    if (restoreFocus) toggle.focus({ preventScroll: true });
  };
  const fail = (message: string, action: () => void) => {
    status.textContent = message;
    retryAction = action;
    retry.hidden = false;
  };

  async function select(result: ParcelSearchResult): Promise<void> {
    stopSearch();
    cancelSelection();
    input.focus({ preventScroll: true });
    window.clearTimeout(debounce);
    const version = ++selectionVersion;
    const request = new AbortController();
    selectionRequest = request;
    retry.hidden = true;
    panel.setAttribute('aria-busy', 'true');
    status.textContent = `Opening parcel ${result.parcelNumber}…`;
    try {
      const params = new URLSearchParams({ parcelNumber: result.parcelNumber });
      const response = await fetch(`/api/parcel?${params}`, { signal: AbortSignal.any([request.signal, AbortSignal.timeout(15000)]) });
      if (!response.ok) throw new Error('Could not open this parcel. Please try again.');
      const parcel = await response.json() as ParcelIdentifyResult;
      if (!parcel?.parcelNumber || !Array.isArray(parcel.boundary?.rings) || !parcel.boundary.rings.length) {
        throw new Error('The county did not return a parcel boundary. Please try again.');
      }
      if (version !== selectionVersion) return;
      status.textContent = completedStatus;
      selectionRequest = null;
      close();
      input.blur();
      options.onSelect(parcel);
    } catch (error) {
      if (version !== selectionVersion || request.signal.aborted) return;
      fail(error instanceof Error && error.name !== 'TimeoutError' ? error.message : 'Opening this parcel timed out. Please try again.', () => void select(result));
    } finally {
      if (version === selectionVersion) {
        selectionRequest = null;
        panel.removeAttribute('aria-busy');
      }
    }
  }

  function appendResults(records: ParcelSearchResult[]): void {
    for (const result of records) {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'search-result';
      for (const [className, text] of [
        ['search-result-address', `${result.address}${result.city ? ` · ${result.city}` : ''}`],
        ['search-result-owner', `Recorded owner: ${result.owner || 'Not provided'}`],
        ['search-result-id', `Parcel ${result.parcelNumber}${result.pin ? ` · PIN ${result.pin}` : ''}`],
      ]) {
        const line = document.createElement('span');
        line.className = className;
        line.textContent = text;
        button.append(line);
      }
      button.addEventListener('click', () => void select(result));
      item.append(button);
      list.append(item);
    }
  }

  async function search(offset = 0): Promise<void> {
    stopSearch();
    cancelSelection();
    // Disabling "Show more" or hiding Retry must not strand focus on the body:
    // focus leaving the search region would otherwise cancel its own request.
    input.focus({ preventScroll: true });
    window.clearTimeout(debounce);
    const query = normalizeSearchQuery(input.value);
    const error = searchQueryError(query);
    setOpen(true);
    retry.hidden = true;
    if (offset === 0) {
      // Discarding the first page also discards its completion marker, so a
      // canceled refresh will be retried when the same query is reopened.
      completedQuery = '';
      completedStatus = '';
      results = [];
      list.replaceChildren();
      nextOffset = null;
      more.hidden = true;
    }
    if (error) {
      status.textContent = error;
      return;
    }
    const version = ++searchVersion;
    const request = new AbortController();
    searchRequest = request;
    panel.setAttribute('aria-busy', 'true');
    more.disabled = true;
    status.textContent = offset ? 'Loading more properties…' : 'Searching county properties…';
    try {
      const params = new URLSearchParams({ q: query, offset: String(offset) });
      const response = await fetch(`/api/parcel-search?${params}`, { signal: AbortSignal.any([request.signal, AbortSignal.timeout(15000)]) });
      if (!response.ok) throw new Error('County property search is unavailable. Please try again.');
      const payload = await response.json() as ParcelSearchResponse;
      if (!Array.isArray(payload?.results)) throw new Error('County property search returned an invalid response.');
      if (version !== searchVersion) return;
      completedQuery = query;
      const oldCount = results.length;
      results.push(...payload.results);
      appendResults(payload.results);
      nextOffset = payload.nextOffset;
      more.hidden = nextOffset === null;
      const freshness = payload.stale ? ' Showing older cached county records.' : '';
      const limit = payload.hasMore && nextOffset === null ? ' Refine your search to see other matches.' : '';
      completedStatus = results.length
        ? `${results.length} ${results.length === 1 ? 'property' : 'properties'} shown.${freshness}${limit}`
        : 'No matching properties. Try a street name, parcel number, PIN, or a different owner name.';
      status.textContent = completedStatus;
      if (offset) list.querySelectorAll<HTMLButtonElement>('button')[oldCount]?.focus();
    } catch (error) {
      if (version !== searchVersion || request.signal.aborted) return;
      fail(error instanceof Error && error.name !== 'TimeoutError' ? error.message : 'County property search timed out. Please try again.', () => void search(offset));
    } finally {
      if (version === searchVersion) {
        searchRequest = null;
        panel.removeAttribute('aria-busy');
        more.disabled = false;
      }
    }
  }

  toggle.addEventListener('click', () => {
    if (!content.hidden) {
      close(true);
      return;
    }
    setOpen(true);
    // Keep focus synchronous with the tap so iOS can open its keyboard.
    input.focus({ preventScroll: true });
  });
  input.addEventListener('input', () => {
    stopSearch();
    cancelSelection();
    results = [];
    completedQuery = '';
    completedStatus = '';
    list.replaceChildren();
    more.hidden = true;
    retry.hidden = true;
    clear.hidden = input.value.length === 0;
    setOpen(true);
    const query = normalizeSearchQuery(input.value);
    status.textContent = query.length < 3 ? 'Type at least 3 characters to search all county parcels.' : 'Waiting to search…';
    if (query.length >= 3) debounce = window.setTimeout(() => void search(), 300);
  });
  input.addEventListener('focus', () => {
    setOpen(true);
    if (!status.textContent) status.textContent = 'Type at least 3 characters to search all county parcels.';
    if (normalizeSearchQuery(input.value).length >= 3 && normalizeSearchQuery(input.value) !== completedQuery) {
      debounce = window.setTimeout(() => void search(), 300);
    }
  });
  form.addEventListener('submit', (event) => { event.preventDefault(); void search(); });
  clear.addEventListener('click', () => {
    stopSearch();
    cancelSelection();
    input.value = '';
    completedQuery = '';
    completedStatus = '';
    results = [];
    list.replaceChildren();
    more.hidden = true;
    retry.hidden = true;
    clear.hidden = true;
    status.textContent = 'Type at least 3 characters to search all county parcels.';
    setOpen(true);
    input.focus();
  });
  more.addEventListener('click', () => { if (nextOffset !== null) void search(nextOffset); });
  retry.addEventListener('click', () => retryAction?.());
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (content.hidden) return;
    const buttons = Array.from(list.querySelectorAll<HTMLButtonElement>('button'));
    if (!buttons.length) return;
    setOpen(true);
    event.preventDefault();
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? index + 1 : index < 0 ? buttons.length - 1 : index - 1;
    if (next < 0) input.focus();
    else buttons[Math.min(next, buttons.length - 1)].focus();
  });
  root.addEventListener('focusout', () => {
    window.setTimeout(() => { if (!root.contains(document.activeElement)) close(); }, 0);
  });
  document.addEventListener('pointerdown', (event) => {
    if (!root.contains(event.target as Node)) close();
  });
  return { close, cancelSelection };
}
